// PoliticaCookiesPage.tsx - Página de Política de Cookies para Algorithmia

import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  DocumentTextIcon, // ← Cambiado de CookieIcon a DocumentTextIcon
  ShieldCheckIcon 
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import LogoAlgorithmia from '../../src/assets/LOGOAZUL-Ajpg.jpg';

const PoliticaCookiesPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <>
     <SEO
  title="Política de Cookies | Algorithmia - Desarrollo de Software en Córdoba"
  description="Política de cookies de Algorithmia. Información sobre el uso de cookies en nuestro sitio web y cómo gestionar tus preferencias de privacidad."
  keywords="política cookies, cookies, preferencias cookies, privacidad navegación"
  url="https://algorithmia.com.ar/politica-cookies"
  canonicalUrl="https://algorithmia.com.ar/politica-cookies"  // ✅ agregado
  noIndex={true}                                               // ✅ agregado
/>


      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
        {/* Header */}
        <nav className="sticky top-0 z-40 py-3 bg-white/95 backdrop-blur-lg border-b border-gray-200 shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <motion.button
              whileHover={{ scale: 1.05, x: -5 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-gray-600 font-medium text-sm hover:bg-gray-100 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
              <span>Volver al Inicio</span>
            </motion.button>
            
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-lg flex items-center justify-center shadow-lg overflow-hidden">
                <img src={LogoAlgorithmia} alt="Algorithmia Logo" className="h-8 w-auto p-0.5" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-[#4ADE80] via-[#3BC571] to-[#2D4A7D] bg-clip-text text-transparent">
                Algorithmia
              </span>
            </div>
          </div>
        </nav>

        {/* Contenido */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100"
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="p-4 bg-gradient-to-r from-[#4ADE80] to-[#3BC571] rounded-2xl shadow-lg">
                <DocumentTextIcon className="h-8 w-8 text-white" /> {/* ← Cambiado aquí */}
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">Política de Cookies</h1>
            </div>

            <p className="text-sm text-gray-500 mb-8 pb-4 border-b border-gray-100">
              Última actualización: {new Date().toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            {/* Resto del contenido igual... */}
            <div className="space-y-8">
              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">1. ¿Qué son las Cookies?</h2>
                <p className="text-gray-700 leading-relaxed">
                  Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo 
                  cuando visitas un sitio web. Permiten recordar tus preferencias y mejorar tu 
                  experiencia de navegación.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">2. Tipos de Cookies que Utilizamos</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Cookies Técnicas (Necesarias)</h3>
                    <p className="text-gray-700">Permiten la navegación y el uso de las diferentes opciones del sitio web.</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Cookies de Análisis</h3>
                    <p className="text-gray-700">Recopilan información sobre el uso del sitio web para mejorar nuestros servicios. Utilizamos Google Analytics.</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">3. Cookies de Terceros</h2>
                <p className="text-gray-700 leading-relaxed">
                  En nuestro sitio web pueden utilizarse servicios de terceros que, por cuenta 
                  propia, instalan cookies. Estos servicios son:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mt-2 space-y-1">
                  <li>Google Analytics (análisis de tráfico)</li>
                  <li>Google Fonts (tipografías)</li>
                  <li>Unsplash (imágenes)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">4. Gestión de Cookies</h2>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Puedes permitir, bloquear o eliminar las cookies instaladas en tu equipo 
                  mediante la configuración de tu navegador:
                </p>
                <ul className="list-disc pl-6 text-gray-700 space-y-1">
                  <li>Chrome: Configuración → Privacidad y seguridad → Cookies</li>
                  <li>Firefox: Opciones → Privacidad y seguridad → Cookies</li>
                  <li>Safari: Preferencias → Privacidad → Cookies</li>
                  <li>Edge: Configuración → Cookies y permisos</li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">5. Cookies Utilizadas</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-gray-700">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left">Cookie</th>
                        <th className="px-4 py-2 text-left">Tipo</th>
                        <th className="px-4 py-2 text-left">Duración</th>
                        <th className="px-4 py-2 text-left">Finalidad</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t">
                        <td className="px-4 py-2">_ga</td>
                        <td className="px-4 py-2">Análisis</td>
                        <td className="px-4 py-2">2 años</td>
                        <td className="px-4 py-2">Diferenciar usuarios</td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-4 py-2">_gid</td>
                        <td className="px-4 py-2">Análisis</td>
                        <td className="px-4 py-2">24 horas</td>
                        <td className="px-4 py-2">Diferenciar usuarios</td>
                      </tr>
                      <tr className="border-t">
                        <td className="px-4 py-2">_gat</td>
                        <td className="px-4 py-2">Análisis</td>
                        <td className="px-4 py-2">1 minuto</td>
                        <td className="px-4 py-2">Limitar peticiones</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section>
                <h2 className="text-xl font-bold text-gray-900 mb-3">6. Consentimiento</h2>
                <p className="text-gray-700 leading-relaxed">
                  Al continuar navegando en nuestro sitio web, consientes el uso de cookies 
                  según lo descrito en esta política.
                </p>
              </section>
            </div>

            <div className="mt-12 p-6 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-gray-700 text-center">
                ¿Tienes dudas sobre nuestra política de cookies? Contáctanos en{' '}
                <a href="mailto:privacidad@algorithmia.com.ar" className="text-[#4ADE80] font-semibold hover:underline">
                  privacidad@algorithmia.com.ar
                </a>
              </p>
            </div>
          </motion.div>
        </div>

        <footer className="py-8 bg-white border-t border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-gray-600 text-sm">
              © {new Date().getFullYear()} Algorithmia. Todos los derechos reservados.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PoliticaCookiesPage;